import { Grid } from '@mui/material';
import { Notes, ProceedSaveLater, SubHeading } from './components';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import MFSelectField from '../../lib/formik/SelectField';
import { Applicant, Broker, BrokerList } from '../../redux-store/types/api-types';
import {
  DLCLMasters,
  grossAnnualMasters,
  mandatorytext,
  occupationDetailsMasters,
  PEPsMasters,
  SAVE_LATER_VALIDATION_CHECK,
  USER_ROLES,
  dateForDematDropdown,
  JointHolderDematValidationDate,
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
} from '../../utils/constant';
import {
  // eslint-disable-next-line
  allowOnlyNumbers,
  applicationComparison,
  clearclIdField,
  cleardpIdField,
  getApplicantName,
  getStep,
  getclIdField,
  getdpIdField,
  isCDSL,
  nameOfBrokerExists,
  otherNameOfBroker,
  saveForLater,
  checkValidationBasedOnDate,
  getDematDropdown,
  checkFirstApplicant,
  sortListKeepOthersLast,
  applyRoleBasedStatus,
  getOlderDate,
  getCurrentStep,
  navigateToNextStep,
} from '../../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { useEffect, useState } from 'react';
import { getBrokers, updateApplication } from '../../redux-store/actions/application';
import { useHistory, useLocation } from 'react-router';
import { KYCDetailsSchema } from '../../utils/schema';
import { MFTextField } from '../../lib/formik';
import { useSnackbar } from 'notistack';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import { useFeatureToggles, useMdmsBrokerListData } from '../../utils/useDataMdms';
import FieldValidationNote from './FieldValidationNote';

export type Values = {
  applicants: Partial<Applicant & { otherNameOfBroker: string }>[];
  saveType: string;
};

const initialValues: Values = {
  applicants: [
    {
      occupationDetails: '',
      grossAnnualIncome: '',
      politicallyExposedPersonStatus: 'Not Applicable',
      ckycNo: '',
      dlclId: '',
      dpId: '',
      clId: '',
      nameOfBroker: '',
      applicant_type: '',
      otherNameOfBroker: '',
    },
  ],
  saveType: 'save and proceed',
};

const KYCDetails = ({
  applicant,
  index,
  values,
  ifTopUp,
}: {
  applicant: string;
  index: number;
  values: Values;
  ifTopUp: boolean;
}): JSX.Element => {
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  return (
    <>
      <SubHeading>
        Additional KYC Details of {applicant} Contributor{index !== 0 && ' ' + index}
      </SubHeading>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.occupationDetails`}
          label="Occupation Details *"
          items={occupationDetailsMasters.map((occupation) => ({
            key: occupation,
            value: occupation,
          }))}
          disabled={ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.grossAnnualIncome`}
          label="Gross Annual Income"
          items={grossAnnualMasters.map((grossIncome) => ({
            key: grossIncome,
            value: grossIncome,
          }))}
          disabled={ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.politicallyExposedPersonStatus`}
          label="Politically Exposed Person (PEP) Status *"
          items={PEPsMasters.map((pep) => ({ key: pep, value: pep }))}
          disabled={ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.ckycNo`}
          label="CKYC Number"
          placeholder={'Enter CKYC number'}
          trimOnBlur={false}
          disabled={[USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role) || ifTopUp}
        />
      </Grid>
    </>
  );
};

export const DematDetails = ({
  values,
  index,
  brokers,
  disabled,
}: {
  values: Values;
  index: number;
  brokers: Broker[];
  disabled: boolean;
}): JSX.Element => {
  const { application } = useSelector((store: RootStateType) => store.application);
  const { createdAt = '', applicationCreatedAt = '' } = application || {};
  return (
    <>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.dlclId`}
          label={`Demat Account Details (for allotment of units) ${
            checkValidationBasedOnDate(
              getOlderDate(createdAt, applicationCreatedAt),
              dateForDematDropdown
            )
              ? '*'
              : ''
          }`}
          items={getDematDropdown(DLCLMasters, getOlderDate(createdAt, applicationCreatedAt)).map(
            (Id) => ({
              key: DLCLMasters[Id],
              value: Id,
            })
          )}
          disabled={disabled}
        />
      </Grid>
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.dpId`}
            label={`${isCDSL(values.applicants[index].dlclId || '') ? 'DPCL ID' : 'DP ID'} *`}
            placeholder={`Enter ${
              isCDSL(values.applicants[index].dlclId || '') ? 'DPCL ID' : 'DP ID'
            }`}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            disabled={disabled}
          />
        </Grid>
      )}
      {getclIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.clId`}
            label="CL ID *"
            placeholder="Enter CL ID"
            disabled={disabled}
          />
        </Grid>
      )}
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <SearchableSelect
            name={`applicants.${index}.nameOfBroker`}
            label="Name of Broker/Depository Participant (DP)"
            items={sortListKeepOthersLast(brokers || [])?.map((broker: Broker) => ({
              key: broker.value,
              value: broker.key,
            }))}
            searchFieldPlaceholder={'Search Broker/Depository Participant'}
            disabled={disabled}
          />
        </Grid>
      )}
      {getdpIdField(values.applicants[index].dlclId || '') &&
        otherNameOfBroker(values.applicants[index].nameOfBroker || '') && (
          <Grid item xs={12} sm={6}>
            <MFTextField
              name={`applicants.${index}.otherNameOfBroker`}
              label="Name of Broker *"
              placeholder="Enter Name of Broker"
              disabled={disabled}
            />
          </Grid>
        )}
    </>
  );
};

export default function AdditionalKYCDetails(): JSX.Element {
  const [kycDetails, setkycDetails] = useState(initialValues);
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [brokerList, setBrokerList] = useState<BrokerList>();
  const { BrokerList } = useMdmsBrokerListData();
  const location = useLocation();
  const path = location.pathname;
  const { toggler } = useFeatureToggles();
  const { isTopup } = application || {};

  useEffect(() => {
    (async function () {
      try {
        setBrokerList(BrokerList as BrokerList);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [BrokerList]);

  useEffect(() => {
    (async function () {
      try {
        const {
          applicants: exisitingApplicants = [],
          createdAt = '',
          applicationCreatedAt = '',
        } = application || {};
        setkycDetails({
          ...kycDetails,
          applicants: exisitingApplicants.map((applicant) => {
            return {
              applicant_type: applicant.applicant_type,
              occupationDetails: applicant.occupationDetails || '',
              grossAnnualIncome: applicant.grossAnnualIncome || '',
              politicallyExposedPersonStatus: applicant.politicallyExposedPersonStatus
                ? applicant.politicallyExposedPersonStatus
                : [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role)
                ? ''
                : 'Not Applicable',
              ckycNo: applicant.ckycNo || '',
              dlclId: applicant.dlclId
                ? applicant.dlclId
                : checkValidationBasedOnDate(
                    getOlderDate(createdAt, applicationCreatedAt),
                    dateForDematDropdown
                  )
                ? ''
                : 'none',
              dpId: applicant.dpId || '',
              clId: applicant.clId || '',
              nameOfBroker: applicant.nameOfBroker
                ? brokerList?.broker_list &&
                  nameOfBrokerExists(
                    brokerList?.broker_list as Broker[],
                    applicant.nameOfBroker || ''
                  )
                  ? applicant.nameOfBroker
                  : 'others'
                : '',
              otherNameOfBroker:
                applicant.nameOfBroker &&
                brokerList?.broker_list &&
                nameOfBrokerExists(
                  brokerList?.broker_list as Broker[],
                  applicant.nameOfBroker || ''
                )
                  ? ''
                  : applicant.nameOfBroker,
            };
          }),
        });
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [application, BrokerList]);

  const handleSubmit = async (values: Values) => {
    try {
      const { applicants, saveType } = values;
      const {
        applicants: exisitingApplicants = [],
        id,
        applicant1ReferenceId = '',
        currentStep,
        status,
        hasPOA,
        applicationNumber,
        topupApplicationNumber,
      } = application || {};
      const updatedApplicants = exisitingApplicants.map((applicant, index) => {
        const { otherNameOfBroker: othersNameOfBroker, ...rest } = applicants[index];
        return {
          ...applicant,
          ...rest,
          clId: clearclIdField(applicants[index].dlclId || '') ? '' : applicants[index].clId,
          dpId: cleardpIdField(applicants[index].dlclId || '')
            ? ''
            : applicants[index]?.dpId?.toUpperCase(),
          nameOfBroker: cleardpIdField(applicants[index].dlclId || '')
            ? ''
            : otherNameOfBroker(applicants[index].nameOfBroker || '')
            ? othersNameOfBroker
            : applicants[index].nameOfBroker,
        };
      });
      const isSaveLater = saveType !== 'save and proceed';
      const checkApplication = applicationComparison(application, {
        ...application,
        applicants: updatedApplicants,
        currentStep:
          !!currentStep &&
          currentStep >
            getCurrentStep(
              application?.applicationType || '',
              path,
              !!isTopup,
              isSaveLater,
              application?.createdAt,
              toggler,
              role
            ) -
              1
            ? currentStep
            : getCurrentStep(
                application?.applicationType || '',
                path,
                !!isTopup,
                isSaveLater,
                application?.createdAt,
                toggler,
                role
              ),
      });

      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              status:
                !hasPOA &&
                AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                status !== 'draft' &&
                applyRoleBasedStatus(role)
                  ? 'sent_to_amc_approver'
                  : status,
              // status: statusRoleBased(role, application),
              currentStep: getCurrentStep(
                application?.applicationType || '',
                path,
                !!isTopup,
                isSaveLater,
                application?.createdAt,
                toggler,
                role
              ),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || '',
            !!isTopup,
            application?.createdAt,
            toggler
          ),
          {
            id,
            applicant1ReferenceId,
            topUpApplication: application?.topupInitiated,
          }
        );
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(
            `Application ${application?.isTopup ? topupApplicationNumber : applicationNumber} - ` +
              ' Saved successfully',
            {
              variant: 'success',
              autoHideDuration: 3000,
            }
          );
        }
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || '',
            !!isTopup,
            application?.createdAt,
            toggler
          ),
          {
            id,
            applicant1ReferenceId,
            topUpApplication: application?.topupInitiated,
          }
        );
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };
  const isFieldDisabled = [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role);
  return (
    <Formik
      initialValues={kycDetails}
      onSubmit={handleSubmit}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            KYCDetailsSchema(
              brokerList?.broker_list as Broker[],
              getOlderDate(application?.createdAt || '', application?.applicationCreatedAt || '')
            ),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues }) => (
        <Grid
          container
          rowSpacing={1}
          // columnSpacing={5}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          <Notes displayContent={mandatorytext} />
          {values.applicants.map((applicant, index) => (
            <>
              <KYCDetails
                applicant={getApplicantName(index + 1)}
                key={index}
                index={index}
                values={values}
                ifTopUp={application?.isTopup || false}
              />

              {((!checkFirstApplicant(applicant?.applicant_type) &&
                checkValidationBasedOnDate(
                  getOlderDate(
                    application?.createdAt || '',
                    application?.applicationCreatedAt || ''
                  ),
                  JointHolderDematValidationDate
                )) ||
                !checkValidationBasedOnDate(
                  getOlderDate(
                    application?.createdAt || '',
                    application?.applicationCreatedAt || ''
                  ),
                  JointHolderDematValidationDate
                )) && (
                <DematDetails
                  values={values}
                  index={index}
                  brokers={brokerList?.broker_list as Broker[]}
                  disabled={isFieldDisabled || false}
                />
              )}
            </>
          ))}
          <FieldValidationNote />
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
